import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  TextField,
  MenuItem,
  Button,
  Tabs,
  Tab,
  Divider,
  Paper,
  Chip,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import PreviewIcon from '@mui/icons-material/Preview';
import TuneIcon from '@mui/icons-material/Tune';

import { useUpdateCurrentCompanyMutation } from '../../../store/api/companyApi';
import { DEFAULT_INVOICE_DESIGN, parseInvoiceDesignSettings } from '../../../shared/utils/invoiceDesignDefaults';
import { CustomDocumentRenderer } from '../../../shared/components/CustomDocumentRenderer';

const PRESET_COLORS = [
  { name: 'Sky Ocean', hex: '#0ea5e9' },
  { name: 'Royal Blue', hex: '#2563eb' },
  { name: 'Emerald', hex: '#059669' },
  { name: 'Teal Forest', hex: '#0d9488' },
  { name: 'Purple Violet', hex: '#7c3aed' },
  { name: 'Ruby Crimson', hex: '#e11d48' },
  { name: 'Charcoal Slate', hex: '#334155' },
  { name: 'Amber Gold', hex: '#d97706' },
];

const THEMES = [
  { id: 'modern', label: 'Modern Accent', desc: 'Clean header bar with vibrant accent styling' },
  { id: 'classic', label: 'Corporate Classic', desc: 'Traditional corporate double-line border design' },
  { id: 'grid', label: 'Tax Invoice Grid', desc: 'Full-cell bordered grid (standard for GCC tax invoices)' },
  { id: 'minimal', label: 'Minimalist Clean', desc: 'Hairline dividers and modern editorial whitespace' },
  { id: 'compact', label: 'Compact 1-Page', desc: 'Tightly spaced layout optimized for 1-page fit' },
];

// Sample preview data
const SAMPLE_ORDER = {
  invoiceNumber: 'INV-2026-0842',
  orderNumber: 'SO-10842',
  orderDate: new Date().toISOString(),
  dueDate: new Date(Date.now() + 14 * 86400000).toISOString(),
  paymentTerms: '14 Days Net',
  customerName: 'Apex International Trading LLC',
  customer: {
    name: 'Apex International Trading LLC',
    nameArabic: 'شركة ابيكس الدولية للتجارة',
    address: 'Building 44, Grand Tower, Doha, Qatar',
    phone: '+974 4455 6677',
    email: 'finance@apexintl.com',
    taxId: 'QA-TRN-984729104',
  },
  referenceNumber: 'PO-99410',
  salesPerson: {
    firstName: 'Hamad',
    lastName: 'Al-Malki',
    email: 'hamad.malki@democompany.com',
    phone: '+974 5512 3456',
  },
  salesperson: 'Hamad Al-Malki',
  items: [
    {
      id: '1',
      name: 'Smart 4K Ultra HD Display Monitor 32"',
      product: { sku: 'MON-4K-32', barcode: '629104829101', unit: 'Pcs' },
      quantity: 2,
      unitPrice: 1200,
      discount: 100,
      vatRate: 5,
      hasWarranty: true,
      warrantyPeriod: { name: '2 Years Manufacturer' },
      serialNumbers: ['SN-MON-98124', 'SN-MON-98125'],
      total: 2415,
    },
    {
      id: '2',
      name: 'Wireless Mechanical Keyboard Pro',
      product: { sku: 'ACC-KEY-WRL', barcode: '629104829102', unit: 'Pcs' },
      quantity: 4,
      unitPrice: 150,
      discount: 0,
      vatRate: 5,
      hasWarranty: true,
      warrantyPeriod: { name: '1 Year Warranty' },
      total: 630,
    },
    {
      id: '3',
      name: 'Ergonomic Vertical Mouse RGB',
      product: { sku: 'ACC-MOU-VRT', barcode: '629104829103', unit: 'Pcs' },
      quantity: 3,
      unitPrice: 80,
      discount: 20,
      vatRate: 5,
      total: 231,
    },
  ],
  subtotal: 3100,
  discountTotal: 120,
  vatTotal: 149,
  totalAmount: 3276,
};

const SAMPLE_QUOTATION = {
  quotationNumber: 'QT-2026-0319',
  quotationDate: new Date().toISOString(),
  validUntil: new Date(Date.now() + 30 * 86400000).toISOString(),
  paymentTerms: '50% Advance, Balance on Delivery',
  customerName: 'Gulf Star Enterprises',
  customer: {
    name: 'Gulf Star Enterprises',
    nameArabic: 'مؤسسة نجمة الخليج',
    address: 'Zone 55, Industrial Area, Doha',
    phone: '+974 5511 2233',
    email: 'info@gulfstarenet.com',
    taxId: 'QA-TRN-112233445',
  },
  referenceNumber: 'RFQ-STAR-2026',
  salesPerson: {
    firstName: 'Rashid',
    lastName: 'Khan',
    email: 'rashid.k@gulfstar.com',
    phone: '+974 5599 8877',
  },
  salesperson: 'Rashid Khan',
  items: [
    {
      id: '1',
      name: 'Enterprise Network Server Rack 42U',
      product: { sku: 'SRV-RCK-42U', barcode: '880192837461', unit: 'Set' },
      quantity: 1,
      unitPrice: 4500,
      discount: 250,
      vatRate: 5,
      hasWarranty: true,
      warrantyPeriod: { name: '3 Years Enterprise' },
      total: 4462.5,
    },
    {
      id: '2',
      name: 'Managed Gigabit 48-Port Switch PoE+',
      product: { sku: 'NET-SW-48P', barcode: '880192837462', unit: 'Unit' },
      quantity: 2,
      unitPrice: 1800,
      discount: 0,
      vatRate: 5,
      hasWarranty: true,
      warrantyPeriod: { name: '2 Years Warranty' },
      total: 3780,
    },
  ],
  subtotal: 8100,
  discountTotal: 250,
  vatTotal: 392.5,
  totalAmount: 8242.5,
};

export function DocumentDesignCustomizer({ company }) {
  const [design, setDesign] = useState(() =>
    parseInvoiceDesignSettings(company?.invoiceDesignSettings)
  );
  const [activeTab, setActiveTab] = useState(0);
  const [previewDocType, setPreviewDocType] = useState('invoice');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [updateCompany, { isLoading: isSaving }] = useUpdateCurrentCompanyMutation();

  useEffect(() => {
    if (company?.invoiceDesignSettings) {
      setDesign(parseInvoiceDesignSettings(company.invoiceDesignSettings));
    }
  }, [company?.invoiceDesignSettings]);

  const handleFieldChange = (field, value) => {
    setDesign((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleColumnToggle = (colKey, checked) => {
    setDesign((prev) => ({
      ...prev,
      columns: {
        ...prev.columns,
        [colKey]: checked,
      },
    }));
  };

  const handleColumnLabelChange = (colKey, value) => {
    setDesign((prev) => ({
      ...prev,
      columnLabels: {
        ...prev.columnLabels,
        [colKey]: value,
      },
    }));
  };

  const handleBankFieldChange = (field, value) => {
    setDesign((prev) => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaveError(null);
      await updateCompany({
        invoiceDesignSettings: JSON.stringify(design),
      }).unwrap();
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err?.data?.message || err?.message || 'Failed to save design settings');
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset all layout and design settings back to default?')) {
      setDesign({ ...DEFAULT_INVOICE_DESIGN });
    }
  };

  return (
    <Box>
      {/* Header with Save / Reset actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}>
            <TuneIcon color="primary" /> Invoice & Quotation Design Studio
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Customize branding, colors, table columns, bank details, and layout for all printed invoices and quotations with live preview.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            disabled={isSaving}
            sx={{ borderRadius: 2 }}
          >
            Reset Default
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isSaving}
            sx={{ borderRadius: 2, px: 3, fontWeight: 700 }}
          >
            {isSaving ? 'Saving...' : 'Save Design'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* ─── LEFT: Customization Controls ───────────────────────────────────── */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <Card sx={{ borderRadius: 2.5, boxShadow: 2 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
              <Tabs
                value={activeTab}
                onChange={(_, val) => setActiveTab(val)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': { fontWeight: 600, fontSize: '0.85rem', textTransform: 'none', minHeight: 48 },
                }}
              >
                <Tab icon={<PaletteIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Style & Colors" />
                <Tab icon={<DescriptionIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Headers & Info" />
                <Tab icon={<ViewColumnIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Table Columns" />
                <Tab icon={<AccountBalanceIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Bank & Terms" />
              </Tabs>
            </Box>

            <CardContent sx={{ p: 2.5, maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }}>
              {/* TAB 0: Style & Colors */}
              {activeTab === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Themes */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Layout Theme
                    </Typography>
                    <Grid container spacing={1.5}>
                      {THEMES.map((thm) => {
                        const selected = design.theme === thm.id;
                        return (
                          <Grid size={{ xs: 12, sm: 6 }} key={thm.id}>
                            <Paper
                              variant="outlined"
                              onClick={() => handleFieldChange('theme', thm.id)}
                              sx={{
                                p: 1.5,
                                cursor: 'pointer',
                                borderRadius: 2,
                                borderColor: selected ? 'primary.main' : 'divider',
                                bgcolor: selected ? 'primary.50' : 'background.paper',
                                borderWidth: selected ? 2 : 1,
                                transition: 'all 0.2s',
                                '&:hover': { borderColor: 'primary.main', bgcolor: 'grey.50' },
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: selected ? 'primary.main' : 'text.primary' }}>
                                {thm.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                                {thm.desc}
                              </Typography>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>

                  <Divider />

                  {/* Primary Color */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Primary Brand Color
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                      {PRESET_COLORS.map((col) => (
                        <Tooltip key={col.hex} title={col.name}>
                          <Box
                            onClick={() => handleFieldChange('primaryColor', col.hex)}
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: '50%',
                              bgcolor: col.hex,
                              cursor: 'pointer',
                              border: design.primaryColor === col.hex ? '3px solid #000' : '2px solid #fff',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                              transform: design.primaryColor === col.hex ? 'scale(1.15)' : 'none',
                              transition: 'transform 0.15s',
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <input
                        type="color"
                        value={design.primaryColor}
                        onChange={(e) => handleFieldChange('primaryColor', e.target.value)}
                        style={{ width: 42, height: 38, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                      />
                      <TextField
                        size="small"
                        label="Custom HEX Color"
                        value={design.primaryColor}
                        onChange={(e) => handleFieldChange('primaryColor', e.target.value)}
                        sx={{ width: 160 }}
                      />
                    </Box>
                  </Box>

                  <Divider />

                  {/* Typography & Spacing */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Typography & Density
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Font Family"
                          value={design.fontFamily}
                          onChange={(e) => handleFieldChange('fontFamily', e.target.value)}
                          SelectProps={{ native: true }}
                        >
                          <option value="Inter, sans-serif">Inter (Modern & Clean)</option>
                          <option value="Roboto, sans-serif">Roboto (Standard)</option>
                          <option value="'Segoe UI', sans-serif">Segoe UI</option>
                          <option value="'Courier New', monospace">Courier (Monospace)</option>
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Layout Density"
                          value={design.density}
                          onChange={(e) => handleFieldChange('density', e.target.value)}
                          SelectProps={{ native: true }}
                        >
                          <option value="compact">Compact (Tighter table)</option>
                          <option value="normal">Normal (Balanced)</option>
                          <option value="spacious">Spacious (Relaxed)</option>
                        </TextField>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              )}

              {/* TAB 1: Headers & Info */}
              {activeTab === 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Document Titles
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Sales Invoice Title"
                        value={design.invoiceTitle}
                        onChange={(e) => handleFieldChange('invoiceTitle', e.target.value)}
                        placeholder="TAX INVOICE"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Quotation Title"
                        value={design.quotationTitle}
                        onChange={(e) => handleFieldChange('quotationTitle', e.target.value)}
                        placeholder="PRICE QUOTATION"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 1 }} />

                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Company Header Elements
                  </Typography>
                  <Grid container spacing={1}>
                    {[
                      { key: 'showLogo', label: 'Company Logo' },
                      { key: 'showCompanyNameEn', label: 'Company Name (English)' },
                      { key: 'showCompanyNameAr', label: 'Company Name (Arabic)' },
                      { key: 'showCompanyAddress', label: 'Company Address' },
                      { key: 'showCompanyPhone', label: 'Phone Number' },
                      { key: 'showCompanyEmail', label: 'Email Address' },
                      { key: 'showCompanyWebsite', label: 'Website URL' },
                      { key: 'showCompanyTaxId', label: 'TRN / Tax Number' },
                    ].map((item) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={item.key}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={!!design[item.key]}
                              onChange={(e) => handleFieldChange(item.key, e.target.checked)}
                            />
                          }
                          label={<Typography variant="body2">{item.label}</Typography>}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  <Divider sx={{ my: 1 }} />

                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Customer & Meta Information
                  </Typography>
                  <Grid container spacing={1}>
                    {[
                      { key: 'showCustomerAddress', label: 'Customer Address' },
                      { key: 'showCustomerPhone', label: 'Customer Phone' },
                      { key: 'showCustomerEmail', label: 'Customer Email' },
                      { key: 'showCustomerTaxId', label: 'Customer TRN / Tax ID' },
                      { key: 'showDocDate', label: 'Document Issue Date' },
                      { key: 'showDueDate', label: 'Due / Validity Date' },
                      { key: 'showPaymentTerms', label: 'Payment Terms' },
                      { key: 'showBarcode', label: 'Barcode / QR code' },
                    ].map((item) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={item.key}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={!!design[item.key]}
                              onChange={(e) => handleFieldChange(item.key, e.target.checked)}
                            />
                          }
                          label={<Typography variant="body2">{item.label}</Typography>}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  <Divider sx={{ my: 1.5 }} />

                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Salesperson Details
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.2 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!!design.showSalesperson}
                            onChange={(e) => handleFieldChange('showSalesperson', e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Show Salesperson on Document</Typography>}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Salesperson Custom Label"
                        placeholder="e.g. Salesperson, Sales Rep, Prepared By"
                        value={design.salespersonLabel ?? 'Salesperson'}
                        onChange={(e) => handleFieldChange('salespersonLabel', e.target.value)}
                        disabled={!design.showSalesperson}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!!design.showSalespersonEmail}
                            onChange={(e) => handleFieldChange('showSalespersonEmail', e.target.checked)}
                            disabled={!design.showSalesperson}
                          />
                        }
                        label={<Typography variant="body2">Show Salesperson Email</Typography>}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!!design.showSalespersonPhone}
                            onChange={(e) => handleFieldChange('showSalespersonPhone', e.target.checked)}
                            disabled={!design.showSalesperson}
                          />
                        }
                        label={<Typography variant="body2">Show Salesperson Phone</Typography>}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label="Salesperson Placement Position"
                        value={design.salespersonPosition || 'header'}
                        onChange={(e) => handleFieldChange('salespersonPosition', e.target.value)}
                        disabled={!design.showSalesperson}
                        helperText="Choose where the salesperson info appears on the printed document"
                      >
                        <MenuItem value="header">📌 Header (Top Right Info Box)</MenuItem>
                        <MenuItem value="customer">🏢 Customer Box (Billing / Reference Section)</MenuItem>
                        <MenuItem value="bottom">✍️ Bottom / Footer (Prepared by Section)</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* TAB 2: Table Columns */}
              {activeTab === 2 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Show / Hide Columns & Custom Labels
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Toggle columns on the invoice/quotation table and rename header labels.
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                    {[
                      { key: 'srNo', defaultLabel: '# / Serial No' },
                      { key: 'sku', defaultLabel: 'Item Code / SKU' },
                      { key: 'barcode', defaultLabel: 'Barcode' },
                      { key: 'description', defaultLabel: 'Item Description' },
                      { key: 'unit', defaultLabel: 'Unit' },
                      { key: 'warranty', defaultLabel: 'Warranty Tag' },
                      { key: 'serialNo', defaultLabel: 'Serial / IMEI Number' },
                      { key: 'quantity', defaultLabel: 'Quantity' },
                      { key: 'unitPrice', defaultLabel: 'Unit Price' },
                      { key: 'discount', defaultLabel: 'Discount' },
                      { key: 'taxRate', defaultLabel: 'VAT / Tax Rate' },
                      { key: 'taxAmount', defaultLabel: 'Tax Amount' },
                      { key: 'total', defaultLabel: 'Line Total' },
                    ].map((col) => {
                      const isChecked = design.columns?.[col.key] !== false;
                      const customLabel = design.columnLabels?.[col.key] || '';
                      return (
                        <Paper
                          key={col.key}
                          variant="outlined"
                          sx={{
                            p: 1.2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderRadius: 1.5,
                            bgcolor: isChecked ? 'background.paper' : 'grey.100',
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={isChecked}
                                onChange={(e) => handleColumnToggle(col.key, e.target.checked)}
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {col.defaultLabel}
                              </Typography>
                            }
                          />
                          {isChecked && (
                            <TextField
                              size="small"
                              placeholder={col.defaultLabel}
                              value={customLabel}
                              onChange={(e) => handleColumnLabelChange(col.key, e.target.value)}
                              sx={{ width: 140, '& input': { py: 0.5, fontSize: '0.8rem' } }}
                            />
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* TAB 3: Bank & Terms */}
              {activeTab === 3 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Banking Information */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Bank & Payment Details
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!!design.showBankDetails}
                            onChange={(e) => handleFieldChange('showBankDetails', e.target.checked)}
                          />
                        }
                        label={<Typography variant="caption">Show on document</Typography>}
                      />
                    </Box>

                    {design.showBankDetails && (
                      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Bank Name"
                            value={design.bankDetails?.bankName || ''}
                            onChange={(e) => handleBankFieldChange('bankName', e.target.value)}
                            placeholder="e.g. Qatar National Bank"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Account Name"
                            value={design.bankDetails?.accountName || ''}
                            onChange={(e) => handleBankFieldChange('accountName', e.target.value)}
                            placeholder="e.g. Company Name LLC"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Account Number"
                            value={design.bankDetails?.accountNumber || ''}
                            onChange={(e) => handleBankFieldChange('accountNumber', e.target.value)}
                            placeholder="e.g. 0012345678"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="IBAN"
                            value={design.bankDetails?.iban || ''}
                            onChange={(e) => handleBankFieldChange('iban', e.target.value)}
                            placeholder="QA00QNBA0000..."
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="SWIFT / BIC"
                            value={design.bankDetails?.swift || ''}
                            onChange={(e) => handleBankFieldChange('swift', e.target.value)}
                            placeholder="QNBAQAQA"
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Terms & Conditions */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Invoice Terms & Conditions
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      value={design.invoiceTerms || ''}
                      onChange={(e) => handleFieldChange('invoiceTerms', e.target.value)}
                      placeholder="1. Goods once sold are not returnable without receipt."
                    />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Quotation Terms & Conditions
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      value={design.quotationTerms || ''}
                      onChange={(e) => handleFieldChange('quotationTerms', e.target.value)}
                      placeholder="1. Price quotation valid for 30 days."
                    />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Default Footer Note
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={design.defaultNotes || ''}
                      onChange={(e) => handleFieldChange('defaultNotes', e.target.value)}
                      placeholder="Thank you for your business!"
                    />
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Signatures */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Signatures & Stamp
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!design.showSignatureBox}
                          onChange={(e) => handleFieldChange('showSignatureBox', e.target.checked)}
                        />
                      }
                      label={<Typography variant="body2">Show Authorized Signature Box</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!design.showCustomerSignature}
                          onChange={(e) => handleFieldChange('showCustomerSignature', e.target.checked)}
                        />
                      }
                      label={<Typography variant="body2">Show Customer Acceptance Box</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!design.showAmountInWords}
                          onChange={(e) => handleFieldChange('showAmountInWords', e.target.checked)}
                        />
                      }
                      label={<Typography variant="body2">Show Total Amount in Words</Typography>}
                    />
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ─── RIGHT: Live Interactive Preview ───────────────────────────────── */}
        <Grid size={{ xs: 12, lg: 7 }}>
          <Card sx={{ borderRadius: 2.5, boxShadow: 2, overflow: 'hidden' }}>
            {/* Preview Document Toggle Bar */}
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'grey.100',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PreviewIcon color="action" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Real-time Document Preview
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label="📄 Sales Invoice"
                  clickable
                  color={previewDocType === 'invoice' ? 'primary' : 'default'}
                  onClick={() => setPreviewDocType('invoice')}
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  label="📝 Quotation"
                  clickable
                  color={previewDocType === 'quotation' ? 'primary' : 'default'}
                  onClick={() => setPreviewDocType('quotation')}
                  sx={{ fontWeight: 700 }}
                />
              </Box>
            </Box>

            {/* Rendered preview container */}
            <Box sx={{ p: 3, bgcolor: '#eef2f6', minHeight: 600, overflowX: 'auto' }}>
              <CustomDocumentRenderer
                docType={previewDocType}
                order={SAMPLE_ORDER}
                quotation={SAMPLE_QUOTATION}
                company={company}
                designSettings={design}
                currency={company?.currency || 'USD'}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Success Notification */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={4000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%', borderRadius: 2, fontWeight: 600 }}>
          🎉 Invoice & Quotation design settings saved successfully!
        </Alert>
      </Snackbar>

      {/* Error Notification */}
      <Snackbar
        open={Boolean(saveError)}
        autoHideDuration={6000}
        onClose={() => setSaveError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ width: '100%', borderRadius: 2 }}>
          {saveError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
